import { useEffect, useMemo, useState } from "react";
import API_BASE from "../../config";
import { avatarColor, initials, photoSrc, canSeeMasterList } from "./StudentProfileModal/profileUtils";
import StudentProfileModal from "./StudentProfileModal/StudentProfileModal";
import s from "./MasterStudentList.module.css";

const STATUSES = [
  { value: "",           label: "All statuses" },
  { value: "ENROLLED",  label: "Enrolled" },
  { value: "PENDING",   label: "Pending" },
  { value: "GRADUATED", label: "Graduated" },
  { value: "INACTIVE",  label: "Inactive" },
];

const STATUS_STYLE = {
  ENROLLED:  { bg: "#e8f6ed", color: "#1e6f3e" },
  PENDING:   { bg: "#fdf3d4", color: "#8a6d28" },
  GRADUATED: { bg: "#e8eef9", color: "#2b4d8a" },
  INACTIVE:  { bg: "#f0f0f0", color: "#777" },
};

const PAGE_SIZE = 30;

/**
 * Master List tab — read-optimised, searchable/filterable view of every
 * student. Clicking a row opens the comprehensive StudentProfileModal.
 *
 * Props come from DirectoryPage which already fetched these lists:
 *   students     Array<Student>
 *   sections     Array<Section>
 *   gradeLevels  Array<GradeLevel>
 *   schoolYears  Array<SchoolYear>
 *   semesters    Array<Semester>
 *   loading      boolean — true while the student list is still loading
 */
export default function MasterStudentList({
  students    = [],
  sections    = [],
  gradeLevels = [],
  schoolYears = [],
  semesters   = [],
  loading     = false,
}) {
  /* All hooks must run unconditionally before any early return. */
  const [search,          setSearch]          = useState("");
  const [statusFilter,    setStatusFilter]    = useState("");
  const [gradeLevelFilter,setGradeLevelFilter]= useState("");
  const [page,            setPage]            = useState(1);
  const [selectedStudent,  setSelectedStudent]  = useState(null);
  const [pendingStudent,   setPendingStudent]   = useState(null);
  /* Local overrides for edited students — keeps the list row in sync without a full refetch */
  const [studentOverrides, setStudentOverrides] = useState({});

  /* After closing current modal, open the queued student cleanly */
  useEffect(() => {
    if (pendingStudent && !selectedStudent) {
      setSelectedStudent(pendingStudent);
      setPendingStudent(null);
    }
  }, [pendingStudent, selectedStudent]);

  function handleStudentUpdate(updated) {
    setSelectedStudent(updated);
    setStudentOverrides(prev => ({ ...prev, [updated.id]: updated }));
  }

  /* ── Lookup maps ─────────────────────────────────────────────────── */
  const sectionById = useMemo(
    () => Object.fromEntries(sections.map(sec => [sec.id, sec])),
    [sections]
  );
  const gradeLevelById = useMemo(
    () => Object.fromEntries(gradeLevels.map(gl => [gl.id, gl])),
    [gradeLevels]
  );
  const sectionToGradeLevel = useMemo(() => {
    const map = {};
    sections.forEach(sec => { map[sec.id] = sec.gradeLevelId; });
    return map;
  }, [sections]);

  /* ── Filter + sort ───────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return students
      .map(st => studentOverrides[st.id] || st)
      .filter(st => {
        if (statusFilter && st.status !== statusFilter) return false;
        if (gradeLevelFilter) {
          const glId = sectionToGradeLevel[st.currentSectionId];
          if (!glId || String(glId) !== String(gradeLevelFilter)) return false;
        }
        if (q) {
          const hay = [
            st.firstName, st.middleName, st.lastName, st.studentNumber,
          ].filter(Boolean).join(" ").toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const la = (a.lastName  || "").toLowerCase();
        const lb = (b.lastName  || "").toLowerCase();
        if (la !== lb) return la < lb ? -1 : 1;
        return (a.firstName || "").toLowerCase()
          .localeCompare((b.firstName || "").toLowerCase());
      });
  }, [students, studentOverrides, search, statusFilter, gradeLevelFilter, sectionToGradeLevel]);

  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows    = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  /* Security gate — after all hooks so the rules-of-hooks invariant holds.
     DirectoryPage already hides this tab for unauthorized roles; this is
     a defence-in-depth check in case the tab is ever mis-wired. */
  if (!canSeeMasterList()) return null;

  function handleSearch(v)      { setSearch(v);          setPage(1); }
  function handleStatus(v)      { setStatusFilter(v);    setPage(1); }
  function handleGradeLevel(v)  { setGradeLevelFilter(v);setPage(1); }

  return (
    <div className={s.root}>
      {/* ── Filter bar ─────────────────────────────────────────────── */}
      <div className={s.filterBar}>
        <div className={s.searchWrap}>
          <span className={s.searchIcon} aria-hidden="true">🔍</span>
          <input
            className={s.searchInput}
            type="text"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search by name or student number…"
            aria-label="Search students"
          />
          {search && (
            <button
              className={s.clearSearch}
              onClick={() => handleSearch("")}
              aria-label="Clear search">
              ✕
            </button>
          )}
        </div>

        <select
          className={s.filterSelect}
          value={gradeLevelFilter}
          onChange={e => handleGradeLevel(e.target.value)}
          aria-label="Filter by grade level">
          <option value="">All grade levels</option>
          {gradeLevels.map(gl => (
            <option key={gl.id} value={gl.id}>{gl.name}</option>
          ))}
        </select>

        <select
          className={s.filterSelect}
          value={statusFilter}
          onChange={e => handleStatus(e.target.value)}
          aria-label="Filter by status">
          {STATUSES.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <div className={s.countPill}>
          {loading
            ? "Loading…"
            : `${filtered.length.toLocaleString()} student${filtered.length !== 1 ? "s" : ""}`}
        </div>
      </div>

      {/* ── Table ──────────────────────────────────────────────────── */}
      <div className={s.tableWrap}>
        {loading ? (
          <SkeletonRows />
        ) : filtered.length === 0 ? (
          <EmptyState hasFilters={!!(search || statusFilter || gradeLevelFilter)} />
        ) : (
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.thPhoto} aria-hidden="true"></th>
                <th>Student #</th>
                <th>Full Name</th>
                <th>Grade Level</th>
                <th>Section</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((st, idx) => {
                const photo     = photoSrc(st.photoUrl);
                const name      = [st.lastName, st.firstName, st.middleName]
                                    .filter(Boolean).join(", ");
                const section   = st.currentSectionId
                                    ? sectionById[st.currentSectionId]
                                    : null;
                const grade     = section?.gradeLevelId
                                    ? gradeLevelById[section.gradeLevelId]
                                    : null;
                const badge     = STATUS_STYLE[st.status] || { bg: "#f0f0f0", color: "#777" };

                return (
                  <tr
                    key={st.id}
                    className={s.row}
                    style={{ animationDelay: `${Math.min(idx, 14) * 22}ms` }}
                    onClick={() => setSelectedStudent(st)}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedStudent(st);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`View full profile of ${name || "student"}`}>

                    <td className={s.tdPhoto} aria-hidden="true">
                      <div
                        className={s.avatar}
                        style={
                          photo
                            ? { backgroundImage: `url(${photo})` }
                            : { backgroundColor: avatarColor(name) }
                        }>
                        {!photo && initials(st.firstName, st.lastName)}
                      </div>
                    </td>

                    <td className={s.tdNum}>
                      <span className={s.numMono}>
                        {st.studentNumber || "—"}
                      </span>
                    </td>

                    <td className={s.tdName}>
                      <span className={s.nameText}>{name || "(Unnamed)"}</span>
                    </td>

                    <td className={s.tdGrade}>
                      {grade ? grade.name : <span className={s.muted}>—</span>}
                    </td>

                    <td className={s.tdSection}>
                      {section ? section.name : <span className={s.muted}>—</span>}
                    </td>

                    <td className={s.tdStatus}>
                      <span
                        className={s.statusBadge}
                        style={{ background: badge.bg, color: badge.color }}>
                        {st.status || "—"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ─────────────────────────────────────────────── */}
      {!loading && totalPages > 1 && (
        <div className={s.pagination}>
          <button
            className={s.pageBtn}
            disabled={currentPage === 1}
            onClick={() => setPage(p => p - 1)}
            aria-label="Previous page">
            ← Prev
          </button>
          <span className={s.pageInfo}>
            Page <b>{currentPage}</b> of <b>{totalPages}</b>
            <span className={s.pageTotal}>
              &nbsp;·&nbsp;{filtered.length.toLocaleString()} total
            </span>
          </span>
          <button
            className={s.pageBtn}
            disabled={currentPage === totalPages}
            onClick={() => setPage(p => p + 1)}
            aria-label="Next page">
            Next →
          </button>
        </div>
      )}

      {/* ── Profile Modal ──────────────────────────────────────────── */}
      <StudentProfileModal
        open={!!selectedStudent}
        student={selectedStudent}
        sections={sections}
        gradeLevels={gradeLevels}
        schoolYears={schoolYears}
        semesters={semesters}
        onClose={() => setSelectedStudent(null)}
        onStudentUpdate={handleStudentUpdate}
        onNavigateToStudent={st => { setSelectedStudent(null); setPendingStudent(st); }}
      />
    </div>
  );
}

/* ── Sub-components ────────────────────────────────────────────────────── */

function SkeletonRows() {
  return (
    <table className={s.table}>
      <thead>
        <tr>
          <th className={s.thPhoto} aria-hidden="true"></th>
          <th>Student #</th>
          <th>Full Name</th>
          <th>Grade Level</th>
          <th>Section</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 8 }).map((_, i) => (
          <tr key={i} className={s.skelRow}>
            <td><span className={`${s.skel} ${s.skelAvatar}`} /></td>
            <td><span className={`${s.skel} ${s.skelSm}`} /></td>
            <td><span className={`${s.skel} ${s.skelLg}`} /></td>
            <td><span className={`${s.skel} ${s.skelMd}`} /></td>
            <td><span className={`${s.skel} ${s.skelMd}`} /></td>
            <td><span className={`${s.skel} ${s.skelSm}`} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EmptyState({ hasFilters }) {
  return (
    <div className={s.emptyState}>
      <div className={s.emptyIcon}>👥</div>
      <div className={s.emptyTitle}>
        {hasFilters ? "No students match your filters" : "No students on record"}
      </div>
      <div className={s.emptySub}>
        {hasFilters
          ? "Try clearing the search or adjusting the filters."
          : "Students added in the Students tab will appear here."}
      </div>
    </div>
  );
}
